import { checkPermissions, useAuth } from "@elit-hair/elit-tools";
import {
    Button,
    Icon,
    SearchBar,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    transitions,
} from "@elit-hair/elit-ui";
import * as Sentry from "@sentry/react";
import { createFileRoute } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbPlus } from "react-icons/tb";

import { ShiftModal } from "@/components/shift-modal";
import { WeeklyShiftCalendar } from "@/components/shifts/weekly-shift-calendar";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FilterValue } from "@/constants/filters";
import { PERMISSIONS } from "@/constants/permissions";
import { useClinic } from "@/providers/hooks/use-clinic";
import { useClinics } from "@/queries/clinic.queries";
import { useCreateShift, useDeleteShift, useShifts, useUpdateShift } from "@/queries/shift.queries";
import { useStaff } from "@/queries/staff.queries";
import { CreateShiftRequest, Shift, ShiftStatus, UpdateShiftRequest } from "@/types/shift.types";
import { fadeInUp } from "@/utils/animations";

function ShiftsPage() {
    const { t } = useTranslation();
    const { selectedClinic } = useClinic();
    const { userData } = useAuth();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isShiftModalOpen, setIsShiftModalOpen] = useState<boolean>(false);
    const [editingShift, setEditingShift] = useState<Shift | null>(null);
    const [selectedStaffId, setSelectedStaffId] = useState<string>(FilterValue.ALL);
    const [selectedStatus, setSelectedStatus] = useState<ShiftStatus | FilterValue>(FilterValue.ALL);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<{
        date: Date;
        hour: number;
        minute: number;
    } | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [shiftToDelete, setShiftToDelete] = useState<string | null>(null);

    const canCreate = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_SHIFTS_CALENDAR]);
    const canEdit = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_SHIFTS_CALENDAR]);
    const canDelete = checkPermissions(userData?.permissionCodes, [PERMISSIONS.TREATMENT_SHIFTS_CALENDAR]);

    // Use selectedClinic from context
    const effectiveClinicId = selectedClinic || undefined;

    // Fetch shifts data
    const {
        data: shiftsData,
        isLoading,
        error,
    } = useShifts({
        clinicId: effectiveClinicId,
        staffId: selectedStaffId !== FilterValue.ALL ? selectedStaffId : undefined,
        status: selectedStatus !== FilterValue.ALL ? selectedStatus : undefined,
    });

    // Fetch clinics and staff for filters
    const { data: clinicsData } = useClinics({});
    const { data: staffData } = useStaff({
        clinicId: selectedClinic || undefined,
    });

    // Mutations
    const deleteShiftMutation = useDeleteShift();
    const createShiftMutation = useCreateShift();
    const updateShiftMutation = useUpdateShift();

    // Filter shifts by search query
    const filteredShifts = useMemo(() => {
        if (!shiftsData?.data) return [];
        if (!searchQuery) return shiftsData.data;

        const query = searchQuery.toLowerCase();
        return shiftsData.data.filter((shift) => {
            const clinic = clinicsData?.data?.find((c) => c.id === shift.clinicId);
            const clinicName = clinic?.name?.toLowerCase() || "";
            const staffId = shift.staffId.toLowerCase();
            return clinicName.includes(query) || staffId.includes(query);
        });
    }, [shiftsData?.data, searchQuery, clinicsData?.data]);

    const handleOpenAddShiftModal = (date?: Date, hour?: number, minute?: number) => {
        setEditingShift(null);
        if (date && hour !== undefined && minute !== undefined) {
            setSelectedTimeSlot({ date, hour, minute });
        } else {
            setSelectedTimeSlot(null);
        }
        setIsShiftModalOpen(true);
    };

    const handleOpenEditShiftModal = (shift: Shift) => {
        setEditingShift(shift);
        setIsShiftModalOpen(true);
    };

    const handleCloseShiftModal = () => {
        setIsShiftModalOpen(false);
        setEditingShift(null);
        setSelectedTimeSlot(null);
    };

    const handleSubmitShift = async (data: CreateShiftRequest | UpdateShiftRequest) => {
        if (editingShift) {
            await updateShiftMutation.mutateAsync({
                id: editingShift.id,
                data: data as UpdateShiftRequest,
            });
        } else {
            await createShiftMutation.mutateAsync(data as CreateShiftRequest);
        }
    };

    const handleDeleteShift = (shiftId: string) => {
        setShiftToDelete(shiftId);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteShift = async () => {
        if (!shiftToDelete) return;

        try {
            await deleteShiftMutation.mutateAsync(shiftToDelete);
            setDeleteDialogOpen(false);
            setShiftToDelete(null);
        } catch (error) {
            Sentry.captureException(error, {
                tags: {
                    component: "ShiftsPage",
                    action: "deleteShift",
                },
            });
        }
    };

    const availableClinics = clinicsData?.data || [];
    const availableStaff = staffData?.data || [];

    return (
        <>
            <div className="h-full flex flex-col p-4 min-h-0">
                <motion.div {...fadeInUp} transition={transitions.slow} className="flex flex-col h-full min-h-0">
                    {/* Header with filters and actions */}
                    <div className="flex flex-col gap-4 mb-6 shrink-0">
                        {/* Filters */}
                        <div className="flex flex-wrap items-center justify-between">
                            <div className="flex items-center gap-4">
                                <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                                    <SelectTrigger className="w-[200px]">
                                        <SelectValue placeholder={t("all_staff")} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={FilterValue.ALL}>{t("all_staff")}</SelectItem>
                                        {availableStaff.map((staff) => (
                                            <SelectItem key={staff.id} value={staff.id}>
                                                {staff.userId}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <Select
                                    value={selectedStatus}
                                    onValueChange={(value) => setSelectedStatus(value as ShiftStatus | FilterValue)}
                                >
                                    <SelectTrigger className="w-[200px]">
                                        <SelectValue placeholder={t("all_statuses")} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={FilterValue.ALL}>{t("all_statuses")}</SelectItem>
                                        <SelectItem value={ShiftStatus.CONFIRMED}>{t("confirmed")}</SelectItem>
                                        <SelectItem value={ShiftStatus.UNCONFIRMED}>{t("unconfirmed")}</SelectItem>
                                    </SelectContent>
                                </Select>
                                {(selectedStaffId !== FilterValue.ALL ||
                                    selectedStatus !== FilterValue.ALL ||
                                    searchQuery) && (
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            setSelectedStaffId(FilterValue.ALL);
                                            setSelectedStatus(FilterValue.ALL);
                                            setSearchQuery("");
                                        }}
                                    >
                                        {t("clear_filters")}
                                    </Button>
                                )}
                            </div>
                            <div className="flex items-center gap-4">
                                <SearchBar
                                    value={searchQuery}
                                    onChange={(value) => setSearchQuery(value)}
                                    debounceMs={300}
                                    placeholder={t("search_shifts")}
                                    className="w-96 md:w-[300px]"
                                />
                                {canCreate && (
                                    <Button variant="default" onClick={() => handleOpenAddShiftModal()}>
                                        <Icon icon={TbPlus} />
                                        {t("add_shift")}
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Weekly Calendar View */}
                    <div className="rounded-md border flex-1 min-h-0">
                        {isLoading ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="text-muted-foreground">{t("loading")}...</div>
                            </div>
                        ) : error ? (
                            <div className="flex items-center justify-center h-full text-red-500">
                                {t("error_loading_shifts")}
                            </div>
                        ) : (
                            <WeeklyShiftCalendar
                                shifts={filteredShifts}
                                clinics={availableClinics}
                                onShiftClick={handleOpenEditShiftModal}
                                onTimeSlotClick={(date, hour, minute) => handleOpenAddShiftModal(date, hour, minute)}
                                onShiftDelete={handleDeleteShift}
                                canCreate={canCreate}
                                canEdit={canEdit}
                                canDelete={canDelete}
                            />
                        )}
                    </div>
                </motion.div>
            </div>
            <ShiftModal
                isOpen={isShiftModalOpen}
                onClose={handleCloseShiftModal}
                shift={editingShift}
                onSubmit={handleSubmitShift}
                initialDateTime={
                    selectedTimeSlot
                        ? {
                              date: selectedTimeSlot.date,
                              hour: selectedTimeSlot.hour,
                              minute: selectedTimeSlot.minute,
                          }
                        : undefined
                }
            />
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("this_action_cannot_be_undone")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteShiftMutation.isPending}>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteShift}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            disabled={deleteShiftMutation.isPending}
                        >
                            {deleteShiftMutation.isPending ? t("deleting") : t("delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export const Route = createFileRoute("/shifts")({
    component: ShiftsPage,
});
